import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { NotebookPen, Loader2, X, ChevronRight } from "lucide-react";

const THESIS_MAX = 240;

/**
 * ThesisGateBanner — D4 (iter134).
 *
 * Renders one card per pending thesis prompt returned by
 * `GET /api/coach/thesis/pending`. Each card asks a single question:
 * "Why this trade?" — one line, ≤240 chars. Optional. Skip just
 * dismisses the card locally (server still surfaces it until the
 * 2-hour grace window expires OR the position closes).
 *
 * Deliberately zero LLM narration — pure user capture. Outcome
 * comparison happens later, on trade close, via the ThesisReview
 * panel.
 *
 * Props:
 *   - variant: "full" (default, /coach) | "mini" (/dashboard)
 *      Mini: single collapsed row with "N thesis prompts pending →"
 *      linking to /coach. Full: expanded cards inline.
 *   - onCaptured?: () => void  — parent may reload other panels.
 */
export default function ThesisGateBanner({ variant = "full", onCaptured }) {
  const [loading, setLoading] = useState(true);
  const [prompts, setPrompts] = useState([]);
  const [dismissed, setDismissed] = useState(() => new Set());
  const [texts, setTexts] = useState({});           // {orderId: string}
  const [saving, setSaving] = useState({});         // {orderId: bool}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/coach/thesis/pending");
      setPrompts(data?.prompts || []);
    } catch {
      setPrompts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setTextFor = (oid, v) =>
    setTexts((prev) => ({ ...prev, [oid]: v.slice(0, THESIS_MAX) }));

  const save = async (p) => {
    const oid = p.entry_order_id;
    const text = (texts[oid] || "").trim();
    if (!text) {
      toast.error("Add one line — or tap Skip.");
      return;
    }
    setSaving((s) => ({ ...s, [oid]: true }));
    try {
      await api.post("/coach/thesis", {
        entry_order_id: oid,
        thesis_text: text,
      });
      toast.success("Thesis captured — compared to outcome on close.");
      setPrompts((prev) => prev.filter((x) => x.entry_order_id !== oid));
      setTexts((prev) => { const c = { ...prev }; delete c[oid]; return c; });
      onCaptured && onCaptured();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Could not save thesis.");
    } finally {
      setSaving((s) => ({ ...s, [oid]: false }));
    }
  };

  const skip = (oid) =>
    setDismissed((prev) => { const n = new Set(prev); n.add(oid); return n; });

  const visible = prompts.filter((p) => !dismissed.has(p.entry_order_id));
  if (loading) return null;
  if (!visible.length) return null;

  // ── MINI variant (Dashboard) ─────────────────────────────────────
  if (variant === "mini") {
    return (
      <a
        href="/coach"
        data-testid="thesis-gate-mini"
        className="flex items-center justify-between gap-3 p-4 border border-[var(--bt-accent)] rounded-sm hover:bg-[var(--bt-surface)] transition-colors group"
      >
        <div className="flex items-center gap-3">
          <NotebookPen className="w-4 h-4 text-[var(--bt-accent)]" />
          <div>
            <p className="bt-label text-[var(--bt-accent)]">
              Thesis {visible.length === 1 ? "prompt" : "prompts"} pending
            </p>
            <p className="text-sm text-[var(--bt-text)] mt-0.5">
              {visible.length === 1
                ? `Log why you took the ${visible[0].symbol} trade.`
                : `${visible.length} open entries waiting for a one-liner.`}
            </p>
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-[var(--bt-text-muted)] group-hover:text-[var(--bt-accent)] transition-colors" />
      </a>
    );
  }

  // ── FULL variant (Coach) ────────────────────────────────────────
  return (
    <section
      data-testid="thesis-gate-banner"
      className="bg-[var(--bt-surface)] border border-[var(--bt-accent)] rounded-sm p-6 space-y-4"
    >
      <div className="flex items-center gap-3">
        <NotebookPen className="w-4 h-4 text-[var(--bt-accent)]" />
        <div>
          <p className="bt-label text-[var(--bt-accent)]">Thesis · Live entries</p>
          <h2 className="font-heading text-xl">Why this trade?</h2>
          <p className="text-xs text-[var(--bt-text-muted)] mt-1 max-w-2xl">
            One line, honest. Coach compares it to the outcome on close —
            no narration, just your words next to your numbers.
          </p>
        </div>
      </div>

      <ul className="space-y-3">
        {visible.map((p) => {
          const val = texts[p.entry_order_id] || "";
          const busy = !!saving[p.entry_order_id];
          const canSave = val.trim().length > 0 && !busy;
          return (
            <li
              key={p.entry_order_id}
              data-testid={`thesis-gate-${p.entry_order_id}`}
              className="border border-[var(--bt-border)] rounded-sm p-4 space-y-3"
            >
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div>
                  <p className="font-heading text-lg text-[var(--bt-text)]">
                    {p.symbol}{" "}
                    <span className="text-sm text-[var(--bt-text-muted)] font-sans">
                      · {p.order_side} · {p.order_size} @ {p.order_price}
                    </span>
                  </p>
                  <p className="text-xs text-[var(--bt-text-muted)] mt-0.5 font-mono">
                    Order #{p.entry_order_id}
                  </p>
                </div>
                <button
                  data-testid={`thesis-skip-btn-${p.entry_order_id}`}
                  onClick={() => skip(p.entry_order_id)}
                  className="text-xs text-[var(--bt-text-muted)] hover:text-[var(--bt-text)] flex items-center gap-1"
                >
                  <X className="w-3 h-3" /> Skip
                </button>
              </div>

              <textarea
                data-testid={`thesis-input-${p.entry_order_id}`}
                value={val}
                onChange={(e) => setTextFor(p.entry_order_id, e.target.value)}
                maxLength={THESIS_MAX}
                placeholder="Setup, level, or emotional read — one line."
                rows={2}
                className="w-full font-sans text-sm leading-relaxed text-white outline-none resize-none"
                style={{
                  background: "#0f1a14",
                  border: "1px solid #1a2e1f",
                  borderRadius: "8px",
                  padding: "12px",
                  transition: "border-color 200ms ease",
                }}
                onFocus={(e) => { e.target.style.borderColor = "#00ff88"; }}
                onBlur={(e) => { e.target.style.borderColor = "#1a2e1f"; }}
              />

              <div className="flex items-center justify-between gap-3">
                <span
                  className="text-xs font-mono"
                  style={{
                    color: val.length >= THESIS_MAX - 20 ? "#fbbf24" : "#94a3b8",
                  }}
                >
                  {val.length} / {THESIS_MAX}
                </span>
                <Button
                  data-testid={`thesis-save-btn-${p.entry_order_id}`}
                  disabled={!canSave}
                  onClick={() => save(p)}
                  className="bg-[var(--bt-accent)] hover:bg-[var(--bt-accent-hover)] text-black font-bold rounded-sm h-9"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save thesis"}
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
